import React, { useState, useEffect } from 'react';
import { TextField, CircularProgress, Box, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IRecipe } from '../../api/IRecipe';
import { searchRecipes } from '../../services/searchService';
import SearchIcon from '@mui/icons-material/Search';

interface RecipeSearchBarProps {
  onSearchResults?: (results: IRecipe[] | null) => void;
}

const RecipeSearchBar: React.FC<RecipeSearchBarProps> = ({ onSearchResults }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (query.length < 1) {
      onSearchResults?.(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await searchRecipes({ keyword: query, published: true });
        if (!cancelled) {
          onSearchResults?.(response.recipes || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Recipe search error:', err);
          onSearchResults?.(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return (
    <Box sx={{ mb: 2, mt: 1, maxWidth: 600 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={t('recipe.searchRecipes')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress color="inherit" size={20} />
            </InputAdornment>
          ) : null,
        }}
      />
    </Box>
  );
};

export default RecipeSearchBar;
